import { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { Font } from '../../constants'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'

Locale.getInstance().registerKeySet('fr', 'lvc-image-input-field', {
  buttonText: 'Ajouter une image*',
  information: 'JPEG ou PNG | min 1920x1080px | 5mb max.',
  errors: {
    message:
      'Veuillez ajouter une description dans le champ “Message” avant de soumettre le rapport.',
    image:
      ' Erreur. Veuillez téléverser une image de moins de 5mb et au format .jpg, .jpeg ou .png.',
    imageResolution: "Erreur. Veuillez vous assurer que l'image fait au minimum 1920x1080px",
    general: 'Une erreur est survenue. Veuillez réessayer plus tard.',
  },
})

Locale.getInstance().registerKeySet('en', 'lvc-image-input-field', {
  buttonText: 'Add an image*',
  information: 'JPEG or PNG | 1920x1080px | 5mb max.',
  errors: {
    message: 'Please provide a description in the “Message” field before submitting the report.',
    image:
      'Error. Please make sure the image does not exceed 5mb and is in .jpg, .jpeg or .png format.',
    imageResolution: 'Error. Please make sure the image is minimum 1920x1080px.',
    general: 'An error occurred. Please try again later.',
  },
})

export interface LvcImageInputField {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
  uploadProgress: number
  isUploading: boolean
}

export class LvcImageInputField extends MinzeElement {
  attrs: Attrs = ['title', 'placeholder', ['default-value', ''], ['on-change', '']]

  reactive: Reactive = [
    ['value', ''],
    ['open', false],
    ['hasImage', false],
    ['imageSrc', ''],
    ['filePath', ''],
    ['selectedImage', null],
    ['uploadProgress', 0],
    ['isUploading', false],
    ['errors', { message: false, general: false, image: false, imageResolution: false }],
  ]

  static observedAttributes = ['title', 'on-change', 'default-value']

  errors: any = {
    message: false,
    general: false,
    image: false,
    imageResolution: false,
  }

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'default-value') {
      this.updateDefaultValue()
    }
  }

  async updateDefaultValue() {
    const value = this.getAttribute('default-value')
    this.imageSrc = value || ''
    this.rerender()
  }

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  onClickUpload(): void {
    // Trigger file input click
    const uploadInput = this.select<HTMLInputElement>('#upload-input')
    uploadInput?.click()
  }

  onCloseButtonClick(): void {
    this.hasImage = false
    this.imageSrc = ''
    this.selectedImage = null
    this.cast('lvc-image-input-field:change', null)
    this.rerender()
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    this.errors.image = false
    this.errors.imageResolution = false
    if (input.files && input.files[0]) {
      const file = input.files[0]
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
      const maxSize = 5 * 1024 * 1024 // 5mb
      const maxHeight = 1080
      const maxWidth = 1920

      if (allowedTypes.includes(file.type) && file.size <= maxSize) {
        try {
          this.isUploading = true
          this.uploadProgress = 0
          this.rerender()

          // Use the progress observer to update the progress bar
          this.filePath = await MainModule.getInstance()
            .getAuth()
            .uploadResumableAnonymousImage(file, (progress: number) => {
              // Ensure progress is a number
              this.uploadProgress = progress
              this.rerender()
            })

          this.isUploading = false
          this.errors.image = false
        } catch (error) {
          this.isUploading = false
          this.errors.image = Locale.getKey('lvc-image-input-field.errors.general')
          this.rerender()
        }

        this.selectedImage = file
        this.hasImage = true

        // get image file resolution and check if it's within the limits
        const image = new Image()
        image.src = URL.createObjectURL(file)
        image.onload = () => {
          if (image.width < maxWidth || image.height < maxHeight) {
            this.errors.imageResolution = Locale.getKey(
              'lvc-image-input-field.errors.imageResolution'
            )
            this.rerender()
            return
          }
          // show image on screen
          this.imageSrc = URL.createObjectURL(file)
          this.onChangeImageSelected(file)
        }
      } else {
        this.errors.image = Locale.getKey('lvc-image-input-field.errors.image')
        this.rerender()
      }
    }
  }

  onChangeImageSelected(image: File): void {
    this.cast('lvc-image-input-field:change', image)
  }

  html = () => `
    <lvc-box class="imageContainer" width="100%" display="flex" position="relative" flexDirection="column" rowGap="16px" background="#ffffff33" justifyContent="center">
      ${
        !this.imageSrc
          ? /*html*/ `
      <div class="content">
        <div class="buttonContainer">
          <lvc-button enable-hover-box-shadow="true" label="${Locale.getKey('lvc-image-input-field.buttonText')}" id="buttonAddImage" name="addImage" font-size="14px" padding="0.5rem 1rem">
            ${Locale.getKey('lvc-image-input-field.buttonText')}
          </lvc-button>
          <input id="upload-input" type="file" accept="image/*" style="display: none;" />
        </div>
        <div class="infoContainer">
          <p class="image-requirements">${Locale.getKey('lvc-image-input-field.information')}</p>
          ${this.errors.image ? `<lvc-error error="${this.errors.image}"></lvc-error>` : ''}
          ${this.errors.imageResolution ? `<lvc-error error="${this.errors.imageResolution}"></lvc-error>` : ''}
        </div>
    
        ${
          this.isUploading
            ? /*html*/ `
        <div class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill" style="width: ${Number(this.uploadProgress)}%;"></div>
          </div>
          <div class="progress-text">${Math.round(this.uploadProgress)}%</div>
        </div>
        `
            : ''
        }
      </div>`
          : /*html*/ `
      <div id="lvc-close-button"><lvc-circle-close-icon></lvc-circle-close-icon></div>
      <img class="image" src="${this.imageSrc}" alt="image">
      `
      }
    </lvc-box>
  `

  css = () => `
    #lvc-close-button {
      cursor: pointer;
      position: absolute;
      top: 8px;
      right: 8px;
    }
    .imageContainer {
      aspect-ratio: 16/9;
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='20' ry='20' stroke='%231E007DB0' stroke-width='2' stroke-dasharray='4%2c 9' stroke-dashoffset='11' stroke-linecap='square'/%3e%3c/svg%3e");
border-radius: 20px;
    }
    .image {
      aspect-ratio: 16/9;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 20px;
    }
    .content{
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

    }

    .image-requirements{
      color: var(--Minuit, #1E007D);
      text-align: center;
      font-size: 12px;
      font-weight: ${Font.weight.medium};
      line-height: 150%;
      opacity: 0.5;
    }

    #buttonAddImage{
      display: flex;
      padding: 13px 16px;
      justify-content: center;
      align-items: center;
      gap: 8px;
      flex: 1 0 0;
    }

    .progress-container {
      width: 80%;
      margin-top: 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .progress-bar {
      width: 100%;
      height: 8px;
      background-color: #ffffff;
      border-radius: 4px;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      background-color: var(--Minuit, #1E007D);
      transition: width 0.3s ease;
    }

    .progress-text {
      margin-top: 4px;
      font-size: 12px;
      color: var(--Minuit, #1E007D);
    }
  `

  eventListeners: EventListeners = [
    [this, 'blur', this.onBlur.bind(this)],
    ['[name="addImage"]', 'click', this.onClickUpload.bind(this)],
    ['#upload-input', 'change', this.onFileSelected.bind(this)],
    ['#lvc-close-button', 'click', this.onCloseButtonClick.bind(this)],
  ]
}
